import React, { useEffect, useState } from "react";
import { X, StickyNote, Home } from "lucide-react";
import toast from "react-hot-toast";
import {
  getProperty,
  getPropertyNotes,
  createPropertyNote,
  deletePropertyNote,
  createAppraisalReminders,
} from "../../api/agent";
import { ownerCommsParams } from "./commsTimeline";
import { fetchCommunicationTimeline } from "../../features/communications/api/communicationsApi";
import AppraisalReminderSeries from "../../features/leads/components/AppraisalReminderSeries";
import AgentNotesPanel from "./AgentNotesPanel";

/**
 * Agent Notes for a PROPERTY row, as a modal (DOMA 13343247771).
 *
 * The Prospects list opens Agent Notes straight from the Last Activity cell for
 * every row. Lead rows already had LeadNotesModal; property rows only had the
 * Agent Notes tab buried inside the property detail page. This surfaces the same
 * shared AgentNotesPanel (manual notes + owner email/SMS history, interleaved)
 * in a popup, so the experience matches the lead modal and is consistent across
 * tabs. "View Property" opens the listing in a new tab, like the lead modal.
 *
 * Notes/comms are fetched here rather than passed in: the pipeline row carries
 * only a trimmed property, and ownerCommsParams needs the owner_email the full
 * record has.
 *
 * George Bottas, Monday 13402350101 (CRM-2026-00126), 28 Oct 2026: working the
 * appraisals list he could write a quick note here but not set a follow up
 * without opening the property and its Agent Notes tab. So the same call-back
 * series that tab has sits under the note box, booked by the same Save button.
 */
export default function PropertyNotesModal({ isOpen, property, onClose }) {
  const propertyId = property?.id || null;
  const [notes, setNotes] = useState([]);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // Call-backs staged under the note, booked with the same Save button.
  const [reminders, setReminders] = useState([]);
  // Bumped after a booking so the series re-reads its "Already booked" list.
  const [reminderKey, setReminderKey] = useState(0);

  const ownerName = property?.owner?.name || property?.person || "";
  const ownerContact = [property?.owner?.contact, property?.owner?.email]
    .filter(Boolean)
    .join(" · ");
  const address = property?.fullAddress || property?.address || "";

  useEffect(() => {
    // Clear on close and at the start of every open so a reopen for a different
    // property can't flash the previous one's notes/comms before the fetch lands.
    setDraft("");
    setReminders([]);
    setNotes([]);
    setComms([]);
    if (!isOpen || !propertyId) return;
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        // Full record first: the row's property is trimmed and has no
        // owner_email, which ownerCommsParams needs for the comms feed.
        const [notesRes, fullRes] = await Promise.all([
          getPropertyNotes(propertyId),
          getProperty(propertyId).catch(() => null),
        ]);
        if (cancelled) return;
        setNotes(notesRes.data?.results || notesRes.data || []);
        const commsParams = ownerCommsParams(fullRes?.data);
        if (commsParams) {
          try {
            const data = await fetchCommunicationTimeline(commsParams);
            if (!cancelled) setComms(Array.isArray(data) ? data : data?.results || []);
          } catch {
            if (!cancelled) setComms([]);
          }
        } else {
          setComms([]);
        }
      } catch {
        if (!cancelled) { setNotes([]); setComms([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, propertyId]);

  const plural = (n) => `${n} reminder${n === 1 ? "" : "s"}`;

  // Books the staged series. Returns how many were booked; never throws, so a
  // note that has already saved is never reported as failed because of it.
  const bookReminders = async (content) => {
    if (reminders.length === 0) return 0;
    try {
      await createAppraisalReminders(
        propertyId,
        reminders.map((entry) => ({
          date: entry.date.toISOString(),
          reason: entry.reason,
        })),
        content
      );
      const booked = reminders.length;
      setReminders([]);
      setReminderKey((k) => k + 1);
      return booked;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          (content
            ? "Note saved, but the reminders could not be booked."
            : "Failed to book the reminders.")
      );
      return 0;
    }
  };

  // Either half on its own is a valid save: a note with no call-back, or a
  // call-back added to a note already written.
  const handleSave = async () => {
    const content = draft.trim();
    if ((!content && reminders.length === 0) || saving) return;
    setSaving(true);
    try {
      if (!content) {
        const booked = await bookReminders("");
        if (booked) toast.success(`${plural(booked)} booked.`);
        return;
      }
      await createPropertyNote(propertyId, { content });
      const res = await getPropertyNotes(propertyId);
      setNotes(res.data?.results || res.data || []);
      setDraft("");
      // The note is safe; now the call-backs, carrying the note as their reason.
      const booked = await bookReminders(content);
      toast.success(booked ? `Note added and ${plural(booked)} booked.` : "Note added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save the note.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await deletePropertyNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {
      toast.error("Failed to delete the note.");
    }
  };

  // A staged reminder counts too: it is not booked until Save.
  const hasUnsaved = draft.trim().length > 0 || reminders.length > 0;
  let saveLabel = "Save Note";
  if (saving) saveLabel = "Saving...";
  else if (!draft.trim() && reminders.length > 0) saveLabel = "Save Reminders";
  else if (reminders.length > 0) saveLabel = "Save Note & Reminders";

  // Don't close on a half-typed note from a stray click, matching LeadNotesModal.
  const requestClose = () => {
    if (hasUnsaved && !window.confirm("Discard your unsaved note and reminders?")) return;
    onClose?.();
  };

  if (!isOpen || !property) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-[#55C5D0]" />
            Agent Notes
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close notes"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            {ownerName && <div className="font-bold text-gray-900 text-sm">{ownerName}</div>}
            {ownerContact && <div className="mt-0.5">{ownerContact}</div>}
            {address && <div className="mt-0.5">{address}</div>}
            {/* New tab on purpose, so a half-typed note isn't lost. */}
            {propertyId && (
              <a
                href={`/properties/${propertyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-[#55C5D0] text-[#128f89] px-2.5 py-1 text-xs font-bold hover:bg-[#55C5D0]/10"
              >
                <Home className="w-3.5 h-3.5" /> View Property
              </a>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="property-note-draft">
              Add a note
            </label>
            <textarea
              id="property-note-draft"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
              placeholder="What happened on this property?"
            />
            {/* The follow-up, directly under the note and booked by the same
                button, as on the property's Agent Notes tab. */}
            <div className="mt-2">
              <AppraisalReminderSeries
                key={`${propertyId}-${reminderKey}`}
                propertyId={propertyId}
                value={reminders}
                onChange={setReminders}
                label="Follow up"
                pendingLabel="Saving with this note"
              />
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasUnsaved || saving}
                className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-4 py-2 rounded-lg font-bold text-sm disabled:opacity-50"
              >
                {saveLabel}
              </button>
            </div>
          </div>

          <AgentNotesPanel
            notes={notes}
            comms={comms}
            loading={loading}
            subject={address}
            onDelete={handleDelete}
            propertyId={propertyId}
          />
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={requestClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
